import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import coin from "@/assets/hai-coin.webp";
import { Seo } from "@/components/Seo";
import { Sparkles, TrendingUp, ShieldCheck, Coins, Brain, Zap } from "lucide-react";

const Index = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const features = [
    { icon: Coins, key: "f1" },
    { icon: Brain, key: "f2" },
    { icon: TrendingUp, key: "f3" },
    { icon: ShieldCheck, key: "f4" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Seo title="HAI — عملة رقمية ذكية | HAI Digital Currency" description="HAI — منصة العملة الرقمية الذكية. تداول، إيداع، وتحويل عملة HAI مع ذكاء سوق مدعوم بالذكاء الاصطناعي." />
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/60 border-b border-primary/10">
        <div className="container flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <img src={coin} alt="HAI" draggable={false} className="h-9 w-9 rounded-full shadow-gold animate-coin-glow select-none" onContextMenu={(e) => e.preventDefault()} />
            <span className="font-bold text-lg text-gradient-gold">{t("brand")}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            {user ? (
              <Link to="/dashboard"><Button variant="default" className="bg-gradient-gold text-primary-foreground">{t("dashboard")}</Button></Link>
            ) : (
              <Link to="/auth"><Button variant="outline" className="gold-border">{t("signIn")}</Button></Link>
            )}
          </div>
        </div>
      </header>

      <main>
      {/* Hero */}
      <section className="relative pt-32 pb-20 container">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 text-center md:text-start">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>HAI Digital Currency</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
              <span className="text-gradient-gold">{t("brand")}</span>
              <br />
              <span className="text-foreground/90 text-3xl md:text-5xl">{t("tagline")}</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl">{t("heroDesc")}</p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <Link to={user ? "/dashboard" : "/auth"}>
                <Button size="lg" className="bg-gradient-gold text-primary-foreground shadow-gold hover:opacity-90">
                  {t("getStarted")} <Zap className="ms-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to={user ? "/deposits" : "/auth"}>
                <Button size="lg" variant="outline" className="gold-border">{t("buyCoin")} 💰</Button>
              </Link>
            </div>
          </div>
          <div className="relative flex items-center justify-center no-copy" onContextMenu={(e) => e.preventDefault()}>
            <div className="absolute inset-0 bg-gradient-radial-gold blur-3xl animate-pulse" />
            <div className="absolute w-72 md:w-[420px] aspect-square rounded-full border border-primary/30 animate-spin-slow" />
            <div className="absolute w-80 md:w-[460px] aspect-square rounded-full border border-primary/20 animate-spin-slow" style={{ animationDirection: "reverse", animationDuration: "26s" }} />
            <img
              src="/hai-coin.webp"
              alt="HAI Coin"
              width="420"
              height="420"
              fetchPriority="high"
              draggable={false}
              className="relative w-72 md:w-[420px] animate-float animate-coin-glow select-none pointer-events-none"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="container py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          <span className="text-gradient-gold">{t("features")}</span>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map(({ icon: Icon, key }) => (
            <div key={key} className="glass-card rounded-2xl p-6 hover:shadow-gold transition-all">
              <div className="h-12 w-12 rounded-xl bg-gradient-gold flex items-center justify-center mb-4 shadow-gold">
                <Icon className="h-6 w-6 text-primary-foreground" />
              </div>
              <h3 className="font-bold text-lg mb-2">{t(`${key}Title`)}</h3>
              <p className="text-sm text-muted-foreground">{t(`${key}Desc`)}</p>
            </div>
          ))}
        </div>
      </section>
      </main>

      <footer className="border-t border-primary/10 py-8 text-center text-sm text-muted-foreground">
        {t("footer")}
      </footer>
    </div>
  );
};

export default Index;
